/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SectionActionButtonComponent } from '@src/app/shared/section-action-button/section-action-button.component';
import { ControlOn } from '@src/app/shared/form/app-form-control';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { GridComponent } from '@src/app/shared/grid/grid.component';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { RelatedRequestsField } from '@src/app/shared/related-requests/related-requests-field';
import { RelatedRequestListDialogComponent } from '@src/app/shared/related-requests/related-request-list-dialog.component';
import { NewRequestType } from '@src/app/modules/crm/new-request-stepper/new-request-stepper.component';
import { NewRequestData, RequestContextService } from '@src/app/modules/crm/request-context.service';
import { FormPillComponent } from '@src/app/shared/form/form-pill/form-pill.component';

type Filter = 'all' | 'open';

@Component({
    selector: 'app-related-requests',
    templateUrl: './related-requests.component.html',
    styleUrls: ['./related-requests.component.scss'],
    imports: [GridComponent, RouterLink, MatIconModule, MatTooltipModule, SectionActionButtonComponent,
        FormPillComponent]
})
export class RelatedRequestsComponent implements FormControlInterface {
    private dialog = inject(MatDialog);
    private reqContextSvc = inject(RequestContextService);

    @Input() control: GridControl;
    @Input() on: ControlOn = 'form';
    field: RelatedRequestsField;

    filter: Filter = 'all';

    private get requests(): ServiceRequest[] { return this.field.srList || []; }

    get total(): number { return this.requests.length; }
    get openCount(): number { return this.requests.filter(r => r.srStatus === ServiceRequest.srOpen.id).length; }

    get visible(): ServiceRequest[] {
        return this.filter === 'open'
            ? this.requests.filter(r => r.srStatus === ServiceRequest.srOpen.id)
            : this.requests;
    }

    toggle(f: Filter): void { this.filter = this.filter === f ? 'all' : f; }

    url(r: ServiceRequest): string { return this.field.navRoute.getIdUrl(r.id); }

    /*
     * The simple <table> renders its pills field-less, so it drives the click itself: run the
     * pill field's flow, drop the refreshed request into the row, then reload the list.
     */
    onStatusClick(r: ServiceRequest): void {
        this.field.statusPillField.gridClick?.(r).subscribe(updated => {
            if (updated) { Object.assign(r, updated); this.field.changed(); }
        });
    }

    onActionStatusClick(r: ServiceRequest): void {
        this.field.actionPillField.gridClick?.(r).subscribe(updated => {
            if (updated) { Object.assign(r, updated); this.field.changed(); }
        });
    }

    /** The "expand" control - the same requests in the full grid, in a big dialog. */
    openBigList(): void {
        this.dialog.open(RelatedRequestListDialogComponent, {
            data: { control: this.control, title: this.field.label },
            autoFocus: false,
            maxWidth: '95vw',
        });
    }

    /**
     * Both add buttons step from the parent request itself, or from the host's newRequestData, so a
     * list that hangs off a unit or contact - which gives them neither - offers no add buttons.
     */
    get canAddChild(): boolean {
        const raisable = !!this.field.parentRequest() || !!this.field.newRequestData;
        return raisable && this.reqContextSvc.canCreate();
    }

    get addCaseTip(): string {
        return this.field.parentRequest()
            ? $localize`Add a case linked to this request`
            : $localize`Add a new case linked to this ${this.field.newRequestNoun}`;
    }

    get addWorkOrderTip(): string {
        return this.field.parentRequest()
            ? $localize`Add a work order for this request`
            : $localize`Add a new work order linked to this ${this.field.newRequestNoun}`;
    }

    /** Linking an existing request rather than raising one - see RelatedRequestsField.linkRequest. */
    get canLink(): boolean {
        return !!this.field.linkRequest;
    }

    get canUnlink(): boolean {
        return !!this.field.unlinkRequest && !this.field.readonly;
    }

    /** Picking, and what linking means, both live on the field - see RelatedRequestsField.link. */
    link(): void {
        this.field.link('userClickedAdd');
    }

    unlink(r: ServiceRequest): void {
        this.field.unlinkRequest(r).subscribe(done => {
            if (done) { this.field.removeRequest(r); }
        });
    }


    /** A child case - office work on the same matter, stepped and saved in one go at the end. */
    addCase(): void {
        this.addChild('case');
    }

    /** A child request for a supplier - stepped, and saved in one go at the end. */
    addWorkOrder(): void {
        this.addChild('workOrder');
    }

    private addChild(requestType: NewRequestType): void {
        // The parent, or the host's data, carries the tenancy, so nothing is asked before the stepper opens.
        this.reqContextSvc.newRequest({ requestType, ...this.raisedFrom() })
            .subscribe(created => {
                if (created) {
                    this.field.changed(created);
                }
            });
    }

    private raisedFrom(): NewRequestData {
        const parent = this.field.parentRequest();
        return parent ? { parent } : this.field.newRequestData();
    }

    public static createComponent(vcr: ViewContainerRef, ctl: GridControl, on: ControlOn) {
        const ref = vcr.createComponent(RelatedRequestsComponent);
        ref.instance.control = ctl;
        ref.instance.on = on;
        ref.instance.field = ctl.field as RelatedRequestsField;
        return ref;
    }

    public static make(options: Partial<RelatedRequestsField> = {}) {
        const field = new RelatedRequestsField(options);
        field.formControlFactory = RelatedRequestsComponent.createComponent;
        return field;
    }
}
